// M12.2 - detectGameFromScene
//
// Heuristica para asociar una escena de terapia con un juego del
// catalogo de motricidad. Evita que el terapeuta tenga que elegir
// manualmente el juego en el dropdown cuando la escena que tiene
// abierta es claramente VR.
//
// Orden de prioridad:
//   1. ?gameKey=xxx en query string del ModOneUrlsala (lo mas robusto:
//      es el mismo gameKey que el juego envia al server).
//   2. Match exacto (case + accent insensitive) entre ModOneTitle y
//      game.label.
//   3. Match por hostname: si el hostname del sceneUrl contiene el
//      gameKey o un token significativo del label (>=4 chars).
//   4. Match parcial por tokens entre titulo y label (>=4 chars en
//      comun). Si varios juegos coinciden, el de mayor solapamiento.
//
// Si nada matchea, retorna null y el linker mantiene el dropdown.

const STOPWORDS = new Set([
  "el",
  "la",
  "los",
  "las",
  "de",
  "del",
  "y",
  "o",
  "a",
  "en",
  "un",
  "una",
  "vr",
  "test",
  "juego",
]);

function foldText(s) {
  return String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

function tokenize(s) {
  // Primero separamos camelCase (LiveStreaming -> Live Streaming) antes
  // de bajar a minusculas, sino se pierde la frontera de mayuscula.
  const split = String(s || "").replace(/([a-z])([A-Z])/g, "$1 $2");
  return foldText(split)
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length >= 4 && !STOPWORDS.has(t));
}

function gameKeyFromUrl(url) {
  if (!url || typeof url !== "string") return null;
  try {
    const u = new URL(url);
    const direct =
      u.searchParams.get("gameKey") || u.searchParams.get("game_key");
    if (direct) return String(direct).toLowerCase();
    return null;
  } catch {
    return null;
  }
}

function hostnameOf(url) {
  if (!url || typeof url !== "string") return "";
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return "";
  }
}

export function detectGameFromScene(games, sceneUrl, sceneTitle) {
  if (!Array.isArray(games) || games.length === 0) return null;

  // 1. ?gameKey=xxx en URL (lo mas robusto)
  const keyFromUrl = gameKeyFromUrl(sceneUrl);
  if (keyFromUrl) {
    const hit = games.find(
      (g) => String(g.gameKey).toLowerCase() === keyFromUrl,
    );
    if (hit) return hit;
  }

  // 2. Match exacto titulo == label
  const foldedTitle = foldText(sceneTitle);
  if (foldedTitle) {
    const hit = games.find((g) => foldText(g.label) === foldedTitle);
    if (hit) return hit;
  }

  // 3. Match por hostname: si el hostname contiene gameKey o un token
  // significativo (>=4 chars, no stopword) del label.
  const host = hostnameOf(sceneUrl);
  if (host) {
    const byHostKey = games.find((g) =>
      host.includes(String(g.gameKey).toLowerCase().replace(/_/g, "")),
    );
    if (byHostKey) return byHostKey;
    const byHostLabel = games.find((g) => {
      const tokens = tokenize(g.label);
      return tokens.some((t) => host.includes(t));
    });
    if (byHostLabel) return byHostLabel;
  }

  // 4. Match parcial por tokens entre titulo y label. Si varios juegos
  // tienen tokens en comun con el titulo, elegimos el de mas tokens
  // compartidos.
  const titleTokens = new Set(tokenize(sceneTitle));
  if (titleTokens.size > 0) {
    let best = null;
    let bestScore = 0;
    for (const g of games) {
      const labelTokens = tokenize(g.label);
      let score = 0;
      for (const lt of labelTokens) {
        if (titleTokens.has(lt)) score += 1;
      }
      if (score > bestScore) {
        bestScore = score;
        best = g;
      }
    }
    if (best) return best;
  }

  return null;
}
